import { javascript } from '@codemirror/lang-javascript';
import { json } from '@codemirror/lang-json';
import { markdown } from '@codemirror/lang-markdown';
import { python } from '@codemirror/lang-python';
import { sql } from '@codemirror/lang-sql';
import { xml } from '@codemirror/lang-xml';
import { drawSelection, EditorView, lineNumbers } from '@codemirror/view';
import { css, Global } from '@emotion/react';
import { useTheme } from '@mui/material';
import { classname } from '@uiw/codemirror-extensions-classname';
import { materialDark } from '@uiw/codemirror-theme-material';
import CodeMirror from '@uiw/react-codemirror';
import { useMemo } from 'react';
import { useParsedValueAndMode } from './parseUtil';

const useDiff = content => {
  return useMemo(() => {
    const lines = (content || '').split('\n');
    const removed = new Set(lines.map((line, idx) => (line.startsWith('-') ? idx : null)).filter(val => val !== null));
    const added = new Set(lines.map((line, idx) => (line.startsWith('+') ? idx : null)).filter(val => val !== null));
    return classname({
      add: lineNumber => {
        if (removed.has(lineNumber - 1)) {
          return 'cm-removed';
        }
        if (added.has(lineNumber - 1)) {
          return 'cm-added';
        }
        return '';
      },
    });
    // biome-ignore lint/correctness/useExhaustiveDependencies: content is used to determine the diff
  }, [content]);
};

const modes = {
  javascript: javascript(),
  json: json(),
  markdown: markdown(),
  xml: xml(),
  python: python(),
  'text/x-sql': sql(),
  'application/json': json(),
  'application/xml': xml(),
  'application/text': [],
};

const CodeEditor = ({ content, mode, width, height, onChange, onFocusChangeCallback, diff, fontSize, editable }) => {
  const { value, mode: parsedMode } = useParsedValueAndMode(content, mode);
  const diffExtension = useDiff(content);
  const theme = useTheme();

  const extensions = useMemo(
    () => [
      lineNumbers(),
      drawSelection(),
      EditorView.lineWrapping,
      modes[mode || parsedMode],
      EditorView.updateListener.of(v => {
        onFocusChangeCallback?.(v.hasFocus);
      }),
      ...(diff ? [diffExtension] : []),
    ],
    [diff, mode, parsedMode, diffExtension, onFocusChangeCallback]
  );
  const diffStyles = useMemo(
    () => css`
      .cm-content {
      ${fontSize ? `font-size: ${fontSize}px;` : ''}
      }
      .cm-added {
        background-color: ${theme.palette.success.main}70;
      }
      .cm-removed {
        background-color: ${theme.palette.error.main}70;
      }

      .cm-layer.cm-selectionLayer .cm-selectionBackground,
      .cm-scroller .ͼ16 .cm-content ::selection,
      .ͼ16 .cm-content .cm-line::selection,
      .ͼ4 .cm-scroller .cm-line ::selection {
        background-color: ${theme.palette.primary.main} !important;
      }

      .cm-selectionMatch {
        background-color: ${theme.palette.primary.main}60 !important;
      }
    `,
    [theme, fontSize]
  );
  return (
    <>
      <Global styles={diffStyles} />
      <CodeMirror
        value={value}
        height={height}
        width={width}
        extensions={extensions}
        theme={materialDark}
        onChange={onChange}
        readOnly={!onChange}
        editable={editable}
      />
    </>
  );
};

export default CodeEditor;
